import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { killProcess, processLoadPageQuery, type ProcessLoadRow } from '../api/devices'
import { toast } from '../ui/notify'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { SimpleLineChart } from '../ui/SimpleLineChart'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Process Load - opteemum/devices/details/processLoad.php: graficul
 * (amChartSimpleLine, #C43626), tabelul HTML apoi DataTable order [[1,'desc']].
 * Kill posteaza process_name si device_id ca Opteemum::killProcess.
 */

const TABLE = 'processesLoad'

const COLUMNS: { key: keyof ProcessLoadRow; title: string; orderable: boolean; numeric?: boolean }[] = [
  { key: 'resource_name', title: 'Process name', orderable: true },
  { key: 'cpu_load', title: 'CPU utilization', orderable: true, numeric: true },
  { key: 'memory_load', title: 'Memory Utilization', orderable: true, numeric: true },
  { key: 'io_per_sec', title: 'Disk I/O', orderable: true, numeric: true },
  { key: 'created_at', title: 'Date', orderable: true },
  { key: 'killed', title: 'Actions', orderable: false },
]

export function DeviceProcessLoad({ id }: ScreenProps) {
  const page = useQuery(processLoadPageQuery(id))
  const [search, setSearch] = useState('')
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 1, dir: 'desc' })

  const filtered = useMemo(() => {
    const rows = page.data?.processes ?? []
    const q = search.trim().toLowerCase()
    const seen = q === '' ? rows : rows.filter((r) => r.resource_name.toLowerCase().includes(q))
    const col = COLUMNS[order.column]
    const sorted = [...seen]
    if (col?.orderable) {
      sorted.sort((a, b) => {
        const left = a[col.key]
        const right = b[col.key]
        const cmp = col.numeric
          ? Number(left) - Number(right)
          : String(left).localeCompare(String(right))
        return order.dir === 'asc' ? cmp : -cmp
      })
    }
    return sorted
  }, [page.data, search, order])

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const chart = page.data.chart
  const slice = filtered.slice(start, start + length)
  const total = filtered.length

  return (
    <div className="device-process-load">
      <label className="color">
        <strong>Process load</strong> status
      </label>
      <div className="processesLoadChart" style={{ width: '100%', height: 150, marginBottom: 20 }}>
        {chart.length === 0 ? (
          <div className="alert alert-info">
            <strong>
              <i className="fas fa-info-circle"></i>
            </strong>{' '}
            Nothing to see yet...
          </div>
        ) : (
          <SimpleLineChart points={chart} unit="Load" color="#C43626" />
        )}
      </div>
      <DetailsTable
        rows={slice}
        total={total}
        search={search}
        start={start}
        length={length}
        order={order}
        onSearch={(q) => {
          setSearch(q)
          setStart(0)
        }}
        onStart={setStart}
        onLength={(n) => {
          setLength(n)
          setStart(0)
        }}
        onOrder={(next) => {
          setOrder(next)
          setStart(0)
        }}
        onKill={async (name) => {
          await killProcess(id, name)
          toast('Kill process instruction sent.', 'Success')
          await page.refetch()
        }}
      />
    </div>
  )
}

function DetailsTable({
  rows,
  total,
  search,
  start,
  length,
  order,
  onSearch,
  onStart,
  onLength,
  onOrder,
  onKill,
}: {
  rows: ProcessLoadRow[]
  total: number
  search: string
  start: number
  length: number
  order: { column: number; dir: 'asc' | 'desc' }
  onSearch(q: string): void
  onStart(n: number): void
  onLength(n: number): void
  onOrder(next: { column: number; dir: 'asc' | 'desc' }): void
  onKill(name: string): Promise<void>
}) {
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${TABLE}_wrapper`}>
      <div className="row">
        <div className="col-sm-6 dataTableF">
          <div id={`${TABLE}_filter`} className="dataTables_filter input-icon">
            <label>
              Search:{' '}
              <input
                type="search"
                className="form-control input-sm"
                aria-controls={TABLE}
                value={search}
                onChange={(e) => onSearch(e.target.value)}
              />
            </label>
          </div>
        </div>
        <div className="col-sm-6 dataTableA"></div>
      </div>
      <div className="row">
        <div className="col-sm-12 userFilters"></div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <table className="processesLoad table table-striped table-hover dataTable no-footer" width="100%" id={TABLE} role="grid">
            <thead>
              <tr role="row">
                {COLUMNS.map((col, i) => {
                  const sorted = order.column === i
                  const cls = [
                    'a-left',
                    col.orderable ? (sorted ? (order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc') : 'sorting') : 'sorting_disabled',
                  ].join(' ')
                  return (
                    <th
                      key={col.key}
                      className={cls}
                      aria-label={`${col.title}: activate to sort column ${sorted && order.dir === 'asc' ? 'descending' : 'ascending'}`}
                      aria-sort={sorted ? (order.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      onClick={() => {
                        if (!col.orderable) return
                        onOrder({ column: i, dir: sorted && order.dir === 'desc' ? 'asc' : 'desc' })
                      }}
                    >
                      {col.title}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={6} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={`${row.resource_name}-${i}`} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                    <td className="td-small">
                      <strong>{row.resource_name}</strong>
                    </td>
                    <td className="td-small" data-order={row.cpu_load}>
                      {row.cpu_text}
                    </td>
                    <td className="td-small" data-order={row.memory_load}>
                      {row.memory_text}
                    </td>
                    <td className="td-small" data-order={row.io_per_sec}>
                      {row.io_text}
                    </td>
                    <td className="td-small">{row.created_at}</td>
                    <td className="td-small">
                      {row.killed ? (
                        <label className="label label-success">Killed</label>
                      ) : (
                        <form
                          className="kill_process"
                          method="post"
                          action="/opteemum/killProcess"
                          onSubmit={(e) => {
                            e.preventDefault()
                            void onKill(row.resource_name)
                          }}
                        >
                          <input name="process_name" value={row.resource_name} type="hidden" />
                          <input name="device_id" value={row.device_id} type="hidden" />
                          <button className="btn btn-danger btn-sm" type="submit">
                            <i className="fas fa-times"></i> Kill
                          </button>
                        </form>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${TABLE}_length`}>
            <label>
              Show{' '}
              <select
                name={`${TABLE}_length`}
                aria-controls={TABLE}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${TABLE}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${TABLE}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`} id={`${TABLE}_previous`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page > 0) onStart((page - 1) * length)
                  }}
                >
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        onStart(b * length)
                      }}
                    >
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${TABLE}_next`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page < pages - 1) onStart((page + 1) * length)
                  }}
                >
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}
